import { compile, type FormatCallback } from 'html-to-text';
import { remark } from 'remark';
import stripMarkdown from 'strip-markdown';

import type { DraftEmailSummary } from './draft-email-event';

// html-to-text handles list layout, but its default formatter ignores reversed
// lists and explicit <li value> numbers. Keep those numbers without owning the
// HTML parser, whitespace handling or table renderer.
const numberedList: FormatCallback = (element, walk, builder) => {
  const items = element.children.filter((child) => child.name === 'li');
  if (!items.length) return;
  const reversed = element.attribs?.reversed != null;
  const defaultStart = reversed ? items.length : 1;
  let ordinal = Number(element.attribs?.start ?? defaultStart);
  if (!Number.isInteger(ordinal)) ordinal = defaultStart;
  const prefixes = items.map((item) => {
    const explicit = Number(item.attribs?.value);
    if (item.attribs?.value != null && Number.isInteger(explicit)) ordinal = explicit;
    const prefix = `${ordinal}. `;
    ordinal += reversed ? -1 : 1;
    return prefix;
  });
  const lineBreaks = element.parent?.name === 'li' ? 1 : 2;
  builder.openList({
    leadingLineBreaks: lineBreaks,
    maxPrefixLength: Math.max(...prefixes.map((prefix) => prefix.length))
  });
  items.forEach((item, index) => {
    builder.openListItem({ prefix: prefixes[index] });
    walk(item.children, builder);
    builder.closeListItem();
  });
  builder.closeList({ trailingLineBreaks: lineBreaks });
};

/** Compile once; previews and both handoff actions share the same plain text. */
const convertHtml = compile({
  wordwrap: false,
  formatters: { numberedList },
  selectors: [
    { selector: 'ul', options: { itemPrefix: '- ' } },
    { selector: 'ol', format: 'numberedList' },
    {
      selector: 'table',
      format: 'dataTable',
      options: { uppercaseHeaderCells: false, maxColumnWidth: undefined }
    },
    // Composer list items and table cells wrap their content in paragraphs.
    ...['li > p', 'td > p', 'th > p'].map((selector) => ({
      selector,
      format: 'block',
      options: { leadingLineBreaks: 1, trailingLineBreaks: 1 }
    })),
    { selector: 'div', options: { leadingLineBreaks: 2, trailingLineBreaks: 2 } },
    ...['h1', 'h2', 'h3', 'h4', 'h5', 'h6'].map((selector) => ({
      selector,
      options: { uppercase: false, leadingLineBreaks: 2, trailingLineBreaks: 2 }
    })),
    {
      selector: 'a',
      options: {
        linkBrackets: ['(', ')'],
        hideLinkHrefIfSameAsText: true,
        // The library removes mailto: before calling this; keep that address,
        // but don't print javascript:, data: or other non-destination links.
        pathRewrite: (href) =>
          /^(https?:|tel:)|^[^:\s@]+@[^\s@]+$/i.test(href.trim()) ? href.trim() : ''
      }
    },
    ...['img', 'script', 'style', 'head', 'title', 'template', 'noscript'].map((selector) => ({
      selector,
      format: 'skip'
    }))
  ]
});

// Remove empty trailing paragraphs, not the padding that aligns table columns.
export const htmlToHandoffText = (html: string): string =>
  convertHtml(html).replace(/^\n+|\n+$/g, '');

const convertMarkdown = remark()
  .use(stripMarkdown, { keep: ['list', 'listItem'] })
  .data('settings', {
    bullet: '-',
    // This output is plain text, not Markdown that needs punctuation escaped again.
    handlers: { text: (node) => node.value }
  });

/** The same readable letter in the non-mailbox card, clipboard and mail client. */
export const emailDraftPlainText = (
  draft: Pick<DraftEmailSummary, 'bodyHtml' | 'bodyText'>
): string =>
  draft.bodyHtml !== undefined
    ? htmlToHandoffText(draft.bodyHtml)
    : convertMarkdown.processSync(draft.bodyText).toString().trim();
